import type {Dispatch} from 'react'
import {FieldError, Label, Radio, type RadioGroupProps} from 'react-aria-components'
import {RadioGroup as RacRadioGroup} from 'react-aria-components'

import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './RadioGroup.module.scss'

interface RadioGroupItem<T extends string> {
  label: string | ReactNode
  value: T
}

interface Props<T extends string> {
  items: readonly RadioGroupItem<T>[]
  label?: string | ReactNode
  error?: {
    message?: string
  }
  onChange?: Dispatch<T>
}

function RadioGroup<T extends string>(
  props: Props<T> & Omit<RadioGroupProps, 'children' | 'onChange'>,
) {
  return (
    <RacRadioGroup
      {...props}
      validationBehavior='aria'
      className={clsx(style.Group, props.className)}
      defaultValue={props.items[0]?.value}
      onChange={props.onChange as RadioGroupProps['onChange']}
    >
      <VisuallyHidden as={Label} className={style.Label}>
        {props.label}
      </VisuallyHidden>
      <div className={style.RadioGroup}>
        {props.items.map(({label, value}) => (
          <Radio key={value} value={value} className={style.Radio}>
            {label}
          </Radio>
        ))}
      </div>
      {props.error?.message && (
        <FieldError className={style.Error}>{props.error.message}</FieldError>
      )}
    </RacRadioGroup>
  )
}

export default memo(RadioGroup) as typeof RadioGroup
